import React, { useEffect, useRef, useState } from 'react';
import { Box, SxProps, IconButton } from '@mui/material';
import { useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMediaQuery } from '@mui/material';
import { Job } from 'types/types';
import { rest } from 'lodash';
import { useAuth } from 'hooks/AuthContext';
import * as Types from 'types/types';
import { useAppState } from 'hooks/GlobalContext';
import { StyledMarkdown } from 'components/StyledMarkdown';
import { JobInfoAdminSection } from './JobInfoAdminSection';
import { JobDetails } from './JobInfoDetails';
import { JobHeader } from './JobInfoHeader';
import { JobRequirements } from './JobInfoRequirements';

interface JobInfoProps {
  job: Job;
  sx?: SxProps;
  action?: string;
  elevation?: number;
  variant?: 'minimal' | 'small' | 'normal' | 'all' | null;
  onClose?: () => void;
  inDialog?: boolean; // Whether this is rendered in a dialog
}

const JobInfo: React.FC<JobInfoProps> = (props: JobInfoProps) => {
  const { setSnack } = useAppState();
  const { user, apiClient } = useAuth();
  const { sx, variant = 'normal', job, onClose, inDialog = false } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) || variant === 'minimal';
  const isAdmin = user?.isAdmin;
  const [adminStatus, setAdminStatus] = useState<string | null>(null);
  const [adminStatusType, setAdminStatusType] = useState<Types.ApiActivityType | null>(null);
  const [activeJob, setActiveJob] = useState<Types.Job>({
    ...job,
  }); /* Copy of job */
  // State for description expansion
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [shouldShowMoreButton, setShouldShowMoreButton] = useState(false);
  const [deleted, setDeleted] = useState<boolean>(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (job && job.id !== activeJob?.id) {
      setActiveJob(job);
    }
  }, [job, activeJob, setActiveJob]);

  // Check if description needs truncation
  useEffect(() => {
    if (descriptionRef.current && job.summary) {
      const element = descriptionRef.current;
      // Check if the scrollHeight is greater than clientHeight (meaning content is truncated)
      setShouldShowMoreButton(element.scrollHeight > element.clientHeight);
    }
  }, [job.summary]);

  const deleteJob = async (jobId: string | undefined): Promise<void> => {
    if (jobId) {
      await apiClient.deleteJob(jobId);
    }
  };

  const handleReset = async (): Promise<void> => {
    setActiveJob({ ...job });
  };

  if (!job) {
    return <Box>No job provided.</Box>;
  }

  const handleSave = async (): Promise<void> => {
    const newJob = await apiClient.updateJob(job.id || '', {
      description: activeJob.description,
      requirements: activeJob.requirements,
    });
    job.updatedAt = newJob.updatedAt;
    setActiveJob(newJob);
    setSnack('Job updated.');
  };

  const handleRefresh = (): void => {
    setAdminStatus('Re-extracting Job information...');
    const jobStatusHandlers = {
      onStatus: (status: Types.ChatMessageStatus): void => {
        console.log('status:', status.content);
        setAdminStatusType(status.activity);
        setAdminStatus(status.content);
      },
      onMessage: async (jobRequirementsMessage: Types.JobRequirementsMessage): Promise<void> => {
        console.log('onMessage - job', jobRequirementsMessage);
        setActiveJob(jobRequirementsMessage.job);
      },
      onError: (error: Types.ChatMessageError): void => {
        console.log('onError', error);
        setAdminStatusType(null);
        setAdminStatus(null);
      },
      onComplete: (): void => {
        setAdminStatusType(null);
        setAdminStatus(null);
      },
    };
    apiClient.regenerateJob(activeJob, jobStatusHandlers);
  };

  return (
    <Box
      className="JobInfo"
      sx={{
        display: 'flex',
        borderColor: 'transparent',
        borderWidth: 2,
        borderStyle: 'solid',
        flexDirection: 'column',
        minWidth: 0,
        maxWidth: '100%',
        opacity: deleted ? 0.5 : 1.0,
        backgroundColor: deleted
          ? theme.palette.action.disabledBackground
          : theme.palette.background.paper,
        pointerEvents: deleted ? 'none' : 'auto',
        ...sx,
      }}
      {...rest}
    >
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          p: 1,
          pb: 0,
          height: '100%',
          flexDirection: 'column',
          alignItems: 'stretch',
          position: 'relative',
        }}
      >
        {onClose && !inDialog && (
          <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        <JobHeader
          activeJob={activeJob}
          isMobile={isMobile}
          variant={variant}
          isDescriptionExpanded={isDescriptionExpanded}
          setIsDescriptionExpanded={setIsDescriptionExpanded}
          shouldShowMoreButton={shouldShowMoreButton}
          descriptionRef={descriptionRef}
          primaryColor={theme.palette.primary.main}
        />

        {variant !== 'small' && variant !== 'minimal' && (
          <JobDetails activeJob={activeJob} job={job} />
        )}
        {variant === 'all' && (
          <StyledMarkdown sx={{ display: 'flex' }} content={activeJob.description} />
        )}

        {variant !== 'small' && variant !== 'minimal' && <JobRequirements activeJob={activeJob} />}

        {isAdmin && (
          <JobInfoAdminSection
            job={job}
            activeJob={activeJob}
            adminStatus={adminStatus}
            adminStatusType={adminStatusType}
            handleSave={handleSave}
            handleReset={handleReset}
            handleRefresh={handleRefresh}
            deleteJob={deleteJob}
            setDeleted={setDeleted}
          />
        )}
      </Box>
    </Box>
  );
};

export { JobInfo };
